import React, { useState, useEffect, useCallback } from "react";
import * as api from "../services/projectApi";
import { fmtDateTime } from "./format";

const SYNC_TONE = { ok: "good", syncing: "warn", error: "bad", never: "muted" };

// Per-platform sync freshness + a manual "Sync now" (spec §16). Read-only on
// ad platforms — this only refreshes our stored copy of the data.
export default function SyncStatusPanel({ projectId, canSync, onSynced }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [probeFrom, setProbeFrom] = useState("");
  const [probeTo, setProbeTo] = useState("");

  const load = useCallback(() => {
    api.getSyncStatus(projectId).then(setRows).catch((e) => setError(e.response?.data?.message || e.message));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.syncProject(projectId);
      load();
      onSynced?.();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runProbe = async () => {
    setProbing(true);
    setError(null);
    setProbe(null);
    try {
      const params = probeFrom && probeTo ? { from: probeFrom, to: probeTo } : undefined;
      setProbe(await api.googleProbe(projectId, params));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setProbing(false);
    }
  };

  return (
    <div className="proj-panel">
      <div className="proj-progress-head">
        <h4 style={{ margin: 0 }}>Data sync</h4>
        {canSync && (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input type="date" value={probeFrom} onChange={(e) => setProbeFrom(e.target.value)} aria-label="Diagnose from" style={{ background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 8, padding: "5px 8px", fontSize: 12, colorScheme: "dark" }} />
            <input type="date" value={probeTo} onChange={(e) => setProbeTo(e.target.value)} aria-label="Diagnose to" style={{ background: "var(--bg2)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 8, padding: "5px 8px", fontSize: 12, colorScheme: "dark" }} />
            <button className="proj-btn" disabled={probing} onClick={runProbe}>
              {probing ? "Diagnosing…" : "Diagnose"}
            </button>
            <button className="proj-btn" disabled={busy} onClick={sync}>
              {busy ? "Syncing…" : "Sync now"}
            </button>
          </div>
        )}
      </div>
      {error && <div className="proj-alert">{error}</div>}
      {probe && (
        <pre
          style={{
            background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8,
            padding: 12, fontSize: 12, overflowX: "auto", whiteSpace: "pre-wrap", color: "var(--text2)",
          }}
        >
          {JSON.stringify(probe, null, 2)}
        </pre>
      )}

      {rows.length === 0 ? (
        <p className="proj-muted">No linked platforms to sync yet.</p>
      ) : (
        <div className="sync-rows">
          {rows.map((r) => (
            <div key={r.platform} className="sync-row">
              <span className="sync-platform">{r.platform === "google" ? "Google Ads" : r.platform === "meta" ? "Meta Ads" : r.platform}</span>
              <span className={`status-chip tone-${SYNC_TONE[r.status] || "muted"}`}>{r.status}</span>
              <span className="proj-muted">last: {fmtDateTime(r.lastSyncedAt)}</span>
              {r.lastError && <span className="sync-err" title={r.lastError}>· {r.lastError.slice(0, 60)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
